import { useEffect, useMemo, useState } from 'react';
import {
  describe,
  fileKey,
  fileUrl,
  formatDate,
  formatSize,
  groupByPlatform,
  groupVersions,
  packageName,
  platformOf,
  rank,
} from './releaseManifest';
import type { Release, ReleaseFile } from './releaseManifest';
import { track } from './analytics';
type Channel = 'direct';

const trackDownload = (file: ReleaseFile, channel: Channel, url: string) =>
  track('download_click', { channel, platform: platformOf(file), destination: url });

function PackageList({
  files,
  recommended,
  channel,
}: {
  files: ReleaseFile[];
  recommended?: ReleaseFile;
  channel: Channel;
}) {
  return (
    <div className="package-groups">
      {groupByPlatform(files).map(([platform, platformFiles]) => (
        <div className="package-group" key={platform}>
          <h4>{platform}</h4>
          <ul className="package-list">
            {platformFiles.map(file => {
              const url = fileUrl(file);
              const isRecommended = recommended && fileKey(file) === fileKey(recommended);
              return (
                <li key={fileKey(file)}>
                  <div>
                    <span className="package-name">
                      {packageName(file)}
                      {isRecommended && <span className="package-tag">Recommended</span>}
                    </span>
                    {describe(file)?.note && <p className="package-note">{describe(file)?.note}</p>}
                    <p className="package-meta">
                      <span>{formatSize(file.size)}</span>
                      {file.sha256 && (
                        <span title={file.sha256}>SHA-256 {file.sha256.slice(0, 12)}…</span>
                      )}
                    </p>
                  </div>
                  {url ? (
                    <a
                      className="button secondary"
                      href={url}
                      onClick={() => trackDownload(file, channel, url)}
                    >
                      Download
                    </a>
                  ) : (
                    <span className="package-meta">Unavailable</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
export function ReleasePanel({
  manifestUrl,
  title,
  description,
  channel = 'direct',
}: {
  manifestUrl?: string;
  title: string;
  description: string;
  /** Which release track the panel lists; reported with every download click. */
  channel?: Channel;
}) {
  const [release, setRelease] = useState<Release>();
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!manifestUrl) return;
    fetch(manifestUrl, { cache: 'no-store' })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(setRelease)
      .catch(() => setError(true));
  }, [manifestUrl]);
  const versions = useMemo(() => groupVersions(release), [release]);
  const current = versions.filter(group => group.current);
  const older = versions.filter(group => !group.current);
  const [latest] = current;
  const currentFiles = current.flatMap(group => group.files);
  // The recommended download is the auto-updating installer of the newest
  // version: the NSIS .exe on Windows, and whatever the manifest leads with
  // elsewhere.
  const recommended = latest?.files.find(file => rank(file) === 0) || latest?.files[0];
  const primary = recommended && fileUrl(recommended);
  const notes = release?.releaseNotes
    ? Array.isArray(release.releaseNotes)
      ? release.releaseNotes
      : [release.releaseNotes]
    : [];
  return (
    <section className="section">
      <div className="container">
        <article className="download-panel">
          <div>
            <div className="eyebrow">Latest release</div>
            <h2>{title}</h2>
            <p>{description}</p>
            <div className="release-meta">
              {release ? (
                <>
                  <span>
                    Version {release.version || '—'}
                    {release.buildNumber ? ` (${release.buildNumber})` : ''}
                  </span>
                  <span>{formatDate(release.publishedAt) || 'Date unavailable'}</span>
                  <span>{formatSize(recommended?.size ?? release.size)}</span>
                  {(recommended?.sha256 || release.sha256) && (
                    <span title={recommended?.sha256 || release.sha256}>
                      SHA-256 {(recommended?.sha256 || release.sha256)?.slice(0, 12)}…
                    </span>
                  )}
                </>
              ) : (
                <span>
                  {error
                    ? 'Release information is temporarily unavailable.'
                    : 'Release details will appear when publishing is configured.'}
                </span>
              )}
            </div>
            {notes.length > 0 && (
              <div className="release-notes">
                <h3>What’s new</h3>
                <ul>
                  {notes.map(n => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div>
            {primary ? (
              <a
                className="button primary"
                href={primary}
                onClick={() => recommended && trackDownload(recommended, channel, primary)}
              >
                Download
              </a>
            ) : (
              <span className="button secondary">Not published yet</span>
            )}
            {recommended && <p className="package-meta">{packageName(recommended)}</p>}
          </div>
          {currentFiles.length > 1 && (
            <div className="release-files">
              <h3>
                {current.length === 1 ? `All packages in ${latest.version}` : 'Packages in use now'}
              </h3>
              {current.map(group => (
                <div className="current-version" key={group.version}>
                  {current.length > 1 && <h4 className="current-version-label">{group.version}</h4>}
                  <PackageList channel={channel} files={group.files} recommended={recommended} />
                </div>
              ))}
            </div>
          )}
          {older.length > 0 && (
            <div className="release-files">
              <h3>Previous versions</h3>
              <p className="package-note">
                Kept for rollback and managed deployment. Install the latest version unless you have
                a reason not to.
              </p>
              {older.map(group => (
                <details className="version-block" key={group.version}>
                  <summary>
                    <span>Version {group.version}</span>
                    <span className="package-meta">
                      {[
                        formatDate(group.publishedAt),
                        `${group.files.length} package${group.files.length === 1 ? '' : 's'}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </summary>
                  <PackageList channel={channel} files={group.files} />
                </details>
              ))}
            </div>
          )}
        </article>
      </div>
    </section>
  );
}
